import { defineStore } from 'pinia'
import { getReports, getReport, deleteReport } from '@/services/reportApi'
import type { Report, ReportListResponse } from '@/types/Report'

interface ReportState {
  // Reports by organization ID
  // Structure: { [orgId]: { [reportUuid]: report } }
  reports: Record<string, Record<string, Report>>
  // Current active report UUID
  activeReportUuid: string | null
  // Current active version (null means latest)
  activeVersion: number | null
}

export const useReportStore = defineStore('report', {
  state: (): ReportState => ({
    reports: {},
    activeReportUuid: null,
    activeVersion: null,
  }),

  getters: {
    // Get all reports for an organization (sorted by newest first)
    getOrgReports: state => (orgId: number | string): Report[] => {
      const reports = Object.values(state.reports[orgId] || {})
      // Sort by created_at descending (newest first)
      return reports.sort((a, b) => {
        const dateA = new Date(a.created_at)
        const dateB = new Date(b.created_at)
        return dateB.getTime() - dateA.getTime()
      })
    },

    // Get a specific report
    getReport: state => (orgId: number | string, reportUuid: string): Report | null => {
      return state.reports[orgId]?.[reportUuid] || null
    },

    // Get active report
    getActiveReport: (state): Report | null => {
      if (!state.activeReportUuid) return null

      // Find the report in any org
      for (const orgId in state.reports) {
        const report = state.reports[orgId][state.activeReportUuid]
        if (report) return report
      }
      return null
    },
  },

  actions: {
    async fetchReports({ orgId }: { orgId: number | string }) {
      console.log('Fetching reports for org:', orgId)

      try {
        const response = await getReports(Number(orgId)) as ReportListResponse

        // Add each report to the store
        response.reports.forEach((report: Report) => {
          this.updateReport({ orgId, report })
        })

        return response.reports
      } catch (error) {
        console.error('Failed to fetch reports:', error)
        throw error
      }
    },

    async fetchReport({ orgId, reportUuid, version = null }: { orgId: number | string, reportUuid: string, version?: number | null }) {
      console.log('Fetching report:', reportUuid, 'version:', version)

      try {
        const report = await getReport(Number(orgId), reportUuid, version) as Report
        this.updateReport({ orgId, report })
        return report
      } catch (error) {
        console.error('Failed to fetch report:', error)
        throw error
      }
    },

    async deleteReportAction({ orgId, reportUuid }: { orgId: number | string, reportUuid: string }) {
      console.log('Deleting report:', reportUuid)

      try {
        await deleteReport(Number(orgId), reportUuid)
        if (this.reports[orgId]) {
          delete this.reports[orgId][reportUuid]
        }

        if (this.activeReportUuid === reportUuid) {
          this.activeReportUuid = null
          this.activeVersion = null
        }
      } catch (error) {
        console.error('Failed to delete report:', error)
        throw error
      }
    },

    setActiveReport(reportUuid: string | null) {
      this.activeReportUuid = reportUuid
    },

    setActiveVersion(version: number | null) {
      this.activeVersion = version
    },

    clearReportData() {
      this.reports = {}
      this.activeReportUuid = null
      this.activeVersion = null
    },

    // Set a report (create or update)
    updateReport({ orgId, report }: { orgId: number | string, report: Report }) {
      if (!this.reports[orgId]) {
        this.reports[orgId] = {}
      }
      this.reports[orgId][report.report_uuid] = report
    },
  },
})
